import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Button, Callout, Field, Input, Loading } from '../components/ui/primitives';

interface Providers {
  microsoft: boolean;
  password: boolean;
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function LoginPage() {
  const { login, ssoError } = useAuth();
  const [providers, setProviders] = useState<Providers | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(ssoError);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<Providers>('/api/auth/providers')
      // An older API without the endpoint only knows passwords.
      .then(setProviders, () => setProviders({ microsoft: false, password: true }));
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  function signInWithMicrosoft() {
    setBusy(true);
    // A full navigation, not fetch: the API redirects the browser to Microsoft.
    window.location.assign('/api/auth/microsoft/start');
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-ink-900">EmailBlast</h1>
          <p className="mt-1 text-sm text-ink-500">Deliverability console</p>
        </div>

        <div className="space-y-4 rounded-xl border border-ink-200 bg-white p-6 shadow-sm">
          {error && <Callout tone="bad">{error}</Callout>}

          {!providers && <Loading label="Loading sign-in options" />}

          {providers?.microsoft && (
            <Button variant="primary" loading={busy} className="w-full" onClick={signInWithMicrosoft}>
              {!busy && <MicrosoftLogo />}
              Sign in with Microsoft
            </Button>
          )}

          {providers?.password && (
            <form onSubmit={onSubmit} className="space-y-4">
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>

              <Field label="Password">
                <Input
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>

              <Button type="submit" variant="primary" loading={busy} className="w-full">
                Sign in
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
